// A folder guest link, from the guest's side: no account, read-only. The
// backend answers only what the link reaches (everything inside, stopping
// at folders that don't inherit — or the folder's own files), so this page
// just walks it: folders open in place, a file opens in the read-only
// preview. Download shows only where the link allows it. Nodes are named by
// their uuid; the link's token is the only credential.

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ChevronRight, Download, Folder, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { FileTypeIcon } from '@/shared/ui/FileTypeIcon.jsx';
import { FilePreview } from '@/shared/ui/FilePreview';
import { publicRequest } from '@/shared/utils/request';
import { apiErrorMessage } from '@/shared/utils/apiError';
import { GUEST_SHARE_CHILDREN } from '@/shared/utils/urls';

export const GuestFolderBrowser = ({ token, root, allowDownload }) => {
  const { t } = useTranslation(['common']);
  const [path, setPath] = useState([{ uuid: root.uuid, name: root.name }]);
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(null);
  const here = path[path.length - 1];

  useEffect(() => {
    let live = true;
    setItems(null);
    setError(null);
    publicRequest
      .get(GUEST_SHARE_CHILDREN, { params: { shareToken: token, ...(here.uuid === root.uuid ? {} : { parentUuid: here.uuid }) } })
      .then((res) => live && setItems(res.data || []))
      .catch((err) => live && setError(apiErrorMessage(err)));
    return () => { live = false; };
  }, [token, here.uuid, root.uuid]);

  if (open) {
    return (
      <div className="space-y-4">
        <Card>
          <CardHeader className="flex flex-row items-center gap-3 p-3">
            <Button variant="ghost" size="sm" onClick={() => setOpen(null)} aria-label={t('common:guestShare.back')}>
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <FileTypeIcon file={open} className="w-8 h-8 shrink-0" />
            <h2 className="flex-1 min-w-0 truncate text-base font-semibold">{open.name}</h2>
            {allowDownload && open.previewUrl && (
              <Button asChild size="sm">
                <a href={open.previewUrl} download={open.name}>
                  <Download className="w-3.5 h-3.5 mr-1.5" />
                  {t('common:editor.download')}
                </a>
              </Button>
            )}
          </CardHeader>
        </Card>
        <FilePreview file={open} shareToken={token} />
      </div>
    );
  }

  return (
    <Card>
      <CardContent className="p-0">
        <nav className="flex flex-wrap items-center gap-1 px-4 py-3 border-b border-border text-sm" aria-label={t('common:guestShare.path')}>
          {path.map((p, i) => (
            <span key={p.uuid} className="flex items-center gap-1">
              {i > 0 && <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />}
              {i < path.length - 1 ? (
                <button type="button" className="hover:underline" onClick={() => setPath(path.slice(0, i + 1))}>{p.name}</button>
              ) : (
                <span className="font-medium">{p.name}</span>
              )}
            </span>
          ))}
        </nav>
        {error && <p className="p-4 text-sm text-destructive">{error}</p>}
        {!items && !error && <div className="flex justify-center p-8"><Loader2 className="w-5 h-5 animate-spin" /></div>}
        {items && items.length === 0 && <p className="p-4 text-sm text-muted-foreground">{t('common:guestShare.empty')}</p>}
        {items && items.length > 0 && (
          <ul className="divide-y divide-border">
            {items.map((node) => (
              <li key={node.uuid}>
                <button
                  type="button"
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-muted"
                  onClick={() => (node.type === 'folder' ? setPath([...path, { uuid: node.uuid, name: node.name }]) : setOpen(node))}
                >
                  {node.type === 'folder' ? <Folder className="w-5 h-5 text-brand shrink-0" /> : <FileTypeIcon file={node} className="w-5 h-5 shrink-0" />}
                  <span className="truncate text-sm">{node.name}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};

export default GuestFolderBrowser;
